import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { Search, Visibility, Upload, Download, CloudUpload } from '@mui/icons-material';
import { getAllCustomers, importCustomersFromExcel } from '../services/customerService';
import { CustomerWithStats, CustomerFilter } from '../types/Customer';
import { formatCurrency, formatDateTime } from '../utils/validators';
import { toast } from 'react-toastify';
import CustomerDetailsDialog from '../components/admin/CustomerDetailsDialog';
import AdminLayout from '../components/admin/AdminLayout';
import { parseCustomersFromExcel, exportCustomersToExcel } from '../utils/excelUtils';
import { getCurrentUser, isSuperAdmin } from '../services/authService';

const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerWithStats[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showDeleted] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithStats | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState<{ 
    total: number; 
    valid: number; 
    duplicates: number; 
    invalid: number;
    errors: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Check if superuser
  const currentUser = getCurrentUser();
  const isSuperUser = isSuperAdmin(currentUser);

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  useEffect(() => {
    // Filter customers based on search term
    if (!searchTerm.trim()) {
      setFilteredCustomers(customers);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = customers.filter(
        (c) =>
          c.name.toLowerCase().includes(searchLower) ||
          c.email.toLowerCase().includes(searchLower) ||
          c.phone?.toLowerCase().includes(searchLower)
      );
      setFilteredCustomers(filtered);
    }
    setPage(0); // Reset to first page on search
  }, [searchTerm, customers]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const filter: CustomerFilter = {
        deleted: showDeleted,
        sortBy: 'name',
        sortOrder: 'asc',
      };
      const data = await getAllCustomers(filter);
      setCustomers(data);
      setFilteredCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Export customers to Excel
  const handleExport = () => {
    try {
      const exportData = customers.filter((c) => !c.deleted);
      const filename = `customers_${new Date().toISOString().split('T')[0]}.xlsx`;
      exportCustomersToExcel(exportData, filename);
      toast.success(`Exported ${exportData.length} customers to Excel`);
    } catch (error) {
      console.error('Error exporting customers:', error);
      toast.error('Failed to export customers');
    }
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setImporting(true);
    setImportPreview(null);

    try {
      // Parse Excel file
      const excelCustomers = await parseCustomersFromExcel(file);
      
      // Get validation summary
      const { validateCustomerImport } = await import('../utils/excelUtils');
      const validation = validateCustomerImport(excelCustomers, customers);

      setImportPreview({
        total: excelCustomers.length,
        valid: validation.valid.length,
        duplicates: validation.duplicates.length,
        invalid: validation.invalid.length,
        errors: [
          ...validation.duplicates.slice(0, 10).map(d => `❌ ${d.name} (${d.email}): ${d.reason}`),
          ...validation.invalid.slice(0, 10).map(i => `⚠️ ${i.name} (${i.email}): ${i.reason}`),
        ],
      });

      setImportDialogOpen(true);
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to parse Excel file');
      setSelectedFile(null);
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Confirm and execute import
  const handleConfirmImport = async () => {
    if (!selectedFile || !currentUser?.email) return;

    setImporting(true);
    try {
      const excelCustomers = await parseCustomersFromExcel(selectedFile);
      const result = await importCustomersFromExcel(excelCustomers, currentUser.email);

      toast.success(
        `Import completed! ${result.success} customers added, ${result.skipped} skipped.`
      );

      if (result.errors.length > 0) {
        console.log('Import warnings:', result.errors);
      }

      // Reload customers
      await loadCustomers();
      setImportDialogOpen(false);
      setSelectedFile(null);
      setImportPreview(null);
    } catch (error) {
      console.error('Error importing customers:', error);
      toast.error('Failed to import customers');
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => {
    setImportDialogOpen(false);
    setSelectedFile(null);
    setImportPreview(null);
  };

  // Calculate stats
  const totalRevenue = customers
    .filter((c) => !c.deleted)
    .reduce((sum, c) => sum + c.totalAmount, 0);
  const activeCustomers = customers.filter((c) => !c.deleted && c.totalPayments > 0).length;

  // Paginated customers
  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Customer Management
          </Typography>
          
          {/* Import/Export buttons (superuser only) */}
          {isSuperUser && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                Import Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExport}
              >
                Export Excel
              </Button>
            </Box>
          )}
        </Box>
      {/* Header Stats */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Total Customers
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {customers.filter((c) => !c.deleted).length}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Active Customers
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
              {activeCustomers}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Total Revenue
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
              {formatCurrency(totalRevenue, 'EUR')}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search & Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Payment Links</TableCell>
                <TableCell align="center">Total Payments</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell align="center">Last Payment</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {searchTerm
                        ? `No customers found matching "${searchTerm}"`
                        : 'No customers yet. They will be created automatically when you create payment links.'}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCustomers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer' },
                      opacity: customer.deleted ? 0.5 : 1,
                    }}
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setIsDetailsDialogOpen(true);
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {customer.name}
                      </Typography>
                      {customer.deleted && (
                        <Chip label="Deleted" size="small" color="error" sx={{ mt: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {customer.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {customer.phone || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={customer.paymentLinksCount}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {customer.pendingLinksCount > 0 && (
                        <Chip
                          label={`${customer.pendingLinksCount} pending`}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {customer.totalPayments}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {formatCurrency(customer.totalAmount, 'EUR')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" color="text.secondary">
                        {customer.lastPaymentDate
                          ? formatDateTime(customer.lastPaymentDate.toDate())
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCustomer(customer);
                          setIsDetailsDialogOpen(true);
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredCustomers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <CustomerDetailsDialog
        open={isDetailsDialogOpen}
        customer={selectedCustomer}
        onClose={() => {
          setIsDetailsDialogOpen(false);
          setSelectedCustomer(null);
        }}
        onUpdated={() => {
          loadCustomers();
          setIsDetailsDialogOpen(false);
          setSelectedCustomer(null);
        }}
      />

      {/* Import Preview Dialog */}
      <Dialog open={importDialogOpen} onClose={handleCancelImport} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUpload />
            <Typography variant="h6">Import Customers from Excel</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {importing && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
          
          {!importing && importPreview && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Import Summary
                </Typography>
                <Typography variant="body2">
                  • Total rows in Excel: <strong>{importPreview.total}</strong>
                  <br />
                  • Valid customers to import: <strong style={{ color: '#2e7d32' }}>{importPreview.valid}</strong>
                  <br />
                  • Duplicates (will skip): <strong style={{ color: '#ed6c02' }}>{importPreview.duplicates}</strong>
                  <br />
                  • Invalid entries: <strong style={{ color: '#d32f2f' }}>{importPreview.invalid}</strong>
                </Typography>
              </Alert>

              {importPreview.valid === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No new customers to import. All entries are either duplicates or invalid.
                </Alert>
              )}

              {importPreview.errors.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                    Skipped/Invalid Entries (showing first 10):
                  </Typography>
                  <List dense sx={{ maxHeight: 300, overflow: 'auto', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    {importPreview.errors.map((error, idx) => (
                      <React.Fragment key={idx}>
                        <ListItem>
                          <ListItemText 
                            primary={error}
                            primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
                          />
                        </ListItem>
                        {idx < importPreview.errors.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}

              {importPreview.valid > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Ready to import <strong>{importPreview.valid}</strong> new customer(s). 
                    They will be marked as "migrated" and assigned to your account.
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelImport} disabled={importing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmImport}
            variant="contained"
            disabled={importing || !importPreview || importPreview.valid === 0}
            startIcon={importing ? <CircularProgress size={20} /> : <Upload />}
          >
            {importing ? 'Importing...' : `Import ${importPreview?.valid || 0} Customers`}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default CustomersPage;
